import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';
import {
    Alert,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { AiSmartReplyContactPlanState } from '../../../../sic-lib/types/Ai';
import { Contact } from '../../../../sic-lib/types/Contact';
import { UserInfo } from '../../../../sic-lib/types/UserConfig';
import SmartReplyDialogItem from './SmartReplyDialogItem';
import { SmartReplySelectPlan } from './SmartReplySelectPlan';

interface Props {
    onCloseHandler: () => void;
    activePlans: AiSmartReplyContactPlanState[] | undefined;
    contact: Contact;
    isDialogOpen: boolean;
    isAdmin: boolean;
    hasAiFeature: boolean;
    users: UserInfo[];
    userName: string;
}

export function SmartReplyDialog({
    onCloseHandler,
    activePlans,
    contact,
    isDialogOpen,
    users,
    isAdmin,
    hasAiFeature,
    userName,
}: Props) {
    const currentUserPlan = useMemo(
        () => activePlans?.find(plan => plan.userName === userName),
        [activePlans, userName],
    );

    const otherUsersPlans = useMemo(
        () => activePlans?.filter(plan => plan.userName !== userName),
        [activePlans, userName],
    );

    const handleClose = () => {
        onCloseHandler();
    };

    return (
        <Dialog open={isDialogOpen} maxWidth="md" onClose={handleClose} fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <PsychologyIcon fontSize="medium" />
                    <Typography variant="h5">AI Smart Reply</Typography>
                    <IconButton
                        onClick={handleClose}
                        sx={theme => {
                            return { ml: 'auto', color: theme.palette.appHeader.contrastText };
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Grid container columnSpacing={2}>
                    <Grid item md={8} xs={12}>
                        <Stack direction="column" display="flex" gap={2}>
                            <Typography variant="h6">My Active Plan</Typography>
                            {currentUserPlan && (
                                <SmartReplyDialogItem
                                    plan={currentUserPlan}
                                    users={users}
                                    isAdmin={isAdmin}
                                    userName={userName}
                                />
                            )}
                            {!currentUserPlan && (
                                <Typography variant="body2" textAlign="center">
                                    No active plan
                                </Typography>
                            )}
                            {otherUsersPlans && otherUsersPlans?.length > 0 && (
                                <>
                                    <Typography variant="h6">{`Other Active Plan${
                                        otherUsersPlans && otherUsersPlans?.length > 1 ? 's' : ''
                                    }`}</Typography>
                                    {otherUsersPlans.map((plan, index) => (
                                        <SmartReplyDialogItem
                                            key={plan.planId}
                                            plan={plan}
                                            users={users}
                                            isAdmin={isAdmin}
                                            userName={userName}
                                        />
                                    ))}
                                    {!otherUsersPlans.length && (
                                        <Typography variant="body2" textAlign="center">
                                            No active plans
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item md={4} xs={12}>
                        <Stack direction="column" gap={1}>
                            <Typography variant="h6" textAlign="center" mb={2}>
                                Assign a New Plan
                            </Typography>
                            {hasAiFeature && (
                                <SmartReplySelectPlan
                                    contact={contact}
                                    isUserAdmin={isAdmin}
                                    userName={userName}
                                />
                            )}
                            {!hasAiFeature && (
                                <Alert severity="info">
                                    To activate an AI Smart Reply plan, you must have the AI Feature
                                    enabled. Please contact support to get started.
                                </Alert>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
